import { Link } from "react-router-dom";
import type { BrandStrategy } from "../../../../shared/types";
import { Badge } from "../ui/Badge";

function strategyState(strategy: BrandStrategy) {
  switch (strategy.confidence) {
    case "confirmed":
      return {
        label: "Confirmed",
        variant: "success" as const,
        guidance: "Use this as the current operating strategy for campaigns, briefs, and review decisions.",
      };
    case "needs_review":
      return {
        label: "Needs review",
        variant: "warning" as const,
        guidance: "The agent changed a previously confirmed strategy. Review it before treating it as canonical.",
      };
    case "draft":
      return {
        label: "Draft",
        variant: "info" as const,
        guidance: "This is an agent-drafted strategy. Confirm it when it matches the brand direction.",
      };
    default:
      return {
        label: strategy.confidence,
        variant: "default" as const,
        guidance: "Review this strategy state before using it operationally.",
      };
  }
}

function formatUpdatedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString();
}

export function StrategyDetail({
  strategy,
  brandId,
  onConfirm,
  confirming,
  confirmError,
}: {
  strategy: BrandStrategy;
  brandId: string;
  onConfirm?: () => void;
  confirming?: boolean;
  confirmError?: string | null;
}) {
  const state = strategyState(strategy);
  const canConfirm = strategy.confidence === "draft" || strategy.confidence === "needs_review";

  return (
    <div className="space-y-6 text-sm text-slate-700">
      <section className="rounded-2xl border border-slate-900 bg-slate-950 p-5 text-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              Canonical strategy
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-white">Overall Brand Strategy</h2>
            <p className="mt-2 text-sm text-slate-300">
              Saved brand strategy, maintained from check-ins, findings, and agent synthesis.
            </p>
          </div>
          <Badge variant={state.variant} className="bg-white/10 text-white">
            {state.label}
          </Badge>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-[1.5fr,1fr]">
          <div className="rounded-xl border border-white/10 bg-white/10 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">North star</p>
            <p className="mt-2 text-lg font-medium text-white">{strategy.north_star}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/10 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Current state</p>
            <p className="mt-2 text-sm text-slate-100">{state.guidance}</p>
            <p className="mt-3 text-xs text-slate-400">
              Updated {formatUpdatedAt(strategy.updated_at)}
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to={`/brand/${brandId}/campaigns`}
            state={{ fromStrategy: true }}
            className="inline-flex rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-400"
          >
            Create campaign →
          </Link>
          {canConfirm && onConfirm ? (
            <button
              type="button"
              onClick={onConfirm}
              disabled={confirming}
              className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {confirming ? "Saving..." : "Looks right"}
            </button>
          ) : null}
          <Link
            to={`/agent/${brandId}`}
            className="inline-flex rounded-lg border border-white/20 px-4 py-2 text-sm font-medium text-white hover:bg-white/10"
          >
            Refine in chat
          </Link>
          <Link
            to={`/brand/${brandId}/campaigns`}
            className="inline-flex rounded-lg border border-white/20 px-4 py-2 text-sm font-medium text-white hover:bg-white/10"
          >
            Build campaigns from pillars
          </Link>
        </div>
        {confirmError ? (
          <p
            role="alert"
            className="mt-3 rounded-lg border border-red-500/40 bg-red-900/40 px-3 py-2 text-sm text-red-200"
          >
            {confirmError}
          </p>
        ) : null}
      </section>

      <div className="grid gap-4 md:grid-cols-3">
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Audience</h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium text-slate-500">Who</p>
              <p className="mt-1 text-slate-900">{strategy.audience.who}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">What they know</p>
              <p className="mt-1 text-slate-900">{strategy.audience.what_they_know}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">What they care about</p>
              <p className="mt-1 text-slate-900">{strategy.audience.what_they_care_about}</p>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Success metric</h3>
          <p className="text-lg font-medium text-slate-900">{strategy.success_metric}</p>
          <p className="mt-3 text-sm text-slate-600">
            Use this as the operator scoreboard when deciding whether campaigns are aligned or just busy.
          </p>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">How to maintain</h3>
          <p className="text-slate-700">
            Add fresh check-ins, let heartbeat synthesis update the saved strategy, then confirm or refine changes here.
          </p>
          <Link to="/home" className="mt-3 inline-flex text-sm font-medium text-blue-600 hover:underline">
            Add a check-in
          </Link>
        </section>
      </div>

      <section>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Strategy pillars</h3>
        <div className="grid gap-3 md:grid-cols-3">
          {strategy.pillars.map((p, index) => (
            <div key={`${index}-${p.name}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Pillar {index + 1}</p>
              <p className="mt-2 font-semibold text-slate-900">{p.name}</p>
              <p className="mt-2 text-slate-700">{p.angle}</p>
              <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Avoid</p>
                <p className="mt-1 text-sm text-slate-700">{p.avoid}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Agent notes</h3>
        <p className="whitespace-pre-wrap text-slate-700">
          {strategy.agent_notes.trim() || "No agent notes recorded for this strategy yet."}
        </p>
      </section>
    </div>
  );
}
